import React, { memo, useRef, useState } from "react";
import { useClickOutside } from "hooks";
import { Input, UncontrolledTooltip } from "reactstrap";
import cx from "classnames";
import { MAX_CUSTOM_PRODUCT_INPUT_LENGTH, EditNodeTypes } from "variables";
import s from "./Styles.module.scss";

type NodeTitleProps = {
  title: string;
  handleUpdateTitle: (productName: string) => void;
  isEditMode: boolean | string;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean | string>>;
  handleToggleEditMode: (type: string | boolean) => void;
  isEditable: boolean;
  className?: string;
  nodeId: string;
};

export const NodeTitle = memo(
  ({
    title,
    handleUpdateTitle,
    isEditMode,
    setIsEditMode,
    handleToggleEditMode,
    isEditable = true,
    className,
    nodeId,
  }: NodeTitleProps) => {
    const [inputValue, setInputValue] = useState(title || "");
    const inputRef = useRef(null);
    const tooltipId = `tooltip-${title?.replace(/[^a-zA-Z0-9]/g, "")}${nodeId}`;

    const onUpdateNode = () => {
      if (inputValue !== title) {
        handleUpdateTitle(inputValue);
      }
      setIsEditMode(false);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        onUpdateNode();
      }
    };

    useClickOutside(inputRef, () => onUpdateNode(), isEditMode as boolean);

    return (
      <div
        className={cx("d-flex flex-column justify-content-center", className)}
        style={{ minWidth: 0 }}
      >
        {isEditMode ? (
          <div
            className={cx(
              s.inputWrapper,
              "d-flex align-items-center border bg-white",
            )}
            ref={inputRef}
          >
            <Input
              className="fw-bolder text-gray-700 ps-2 fs-md border-0"
              type="text"
              bsSize="sm"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              style={{ maxWidth: "calc(100% - 60px)" }}
              maxLength={MAX_CUSTOM_PRODUCT_INPUT_LENGTH}
              onKeyDown={handleInputKeyDown}
              data-testid="folder-title-input"
            />
            <div
              className={cx(
                s.inputLengthLabel,
                "text-gray-400 d-flex flex-nowrap",
              )}
            >
              <span className="fw-bolder">{inputValue.length}</span>
              <span>&nbsp;/ {MAX_CUSTOM_PRODUCT_INPUT_LENGTH}</span>
            </div>
          </div>
        ) : (
          <>
            <span
              id={tooltipId}
              className={cx("fw-bold fs-md text-truncate", s.title, {
                "cursor-pointer": isEditable,
              })}
              onClick={() =>
                isEditable && handleToggleEditMode(EditNodeTypes.TITLE)
              }
              data-testid="folder-title"
            >
              {title}
            </span>
            <UncontrolledTooltip target={tooltipId} placement="top">
              {title}
            </UncontrolledTooltip>
          </>
        )}
      </div>
    );
  },
);
